import { toDateOnly } from '@utils/date-only';

/** In-memory price of one grid cell; either field may be blank while the user is typing. */
export interface CellPrice {
  purchasePrice: number | null;
  sellingPrice: number | null;
}

/** Which of a cell's two prices an edit targets. */
export type PriceField = keyof CellPrice;

/**
 * Coordinates identifying one cell of a pricing grid. Each grid declares its own labelled tuple — see
 * `ExclusiveCoords` (season × stay type) and `CombinationCoords` (…× anglers × non-anglers).
 */
export type GridCoords = readonly number[];

/** A fully-priced cell, flattened out of the editable map so a store can shape it into its own DTO. */
export interface FilledCell {
  /** The cell's coordinates, in the order its key was built from. */
  coords: number[];
  purchasePrice: number;
  sellingPrice: number;
}

/**
 * A price list (year) of the current water, as the shared price-list dropdown needs it. Both the water's
 * `WaterPriceList` and the combination grid's `WaterPriceListOption` satisfy it.
 */
export interface PriceListOption {
  id: number;
  name: string;
  /** ISO date string (backend DateOnly), e.g. "2026-01-01". */
  startDate: string;
}

/**
 * A price list added to a grid's dropdown options, keeping the newest-first order the API returns and
 * every grid's "default to the first entry" relies on. A newly created price list is not necessarily the
 * newest — a year can be filled in after a later one was already staged — so it cannot just be
 * prepended. ISO date strings sort chronologically, so comparing them as text is the whole ordering.
 */
export function withPriceListInOrder<T extends PriceListOption>(priceLists: readonly T[], added: T): T[] {
  return [...priceLists, added].sort((a, b) => b.startDate.localeCompare(a.startDate));
}

/**
 * Whether a price list has not taken effect yet, i.e. it is staged for a future season.
 *
 * Compared as `yyyy-MM-dd` text rather than as `Date`s on purpose: the API's `DateOnly` carries no time or
 * zone, so parsing it yields UTC midnight while a local "today" does not, and east of Greenwich a list
 * starting *today* would come out as future.
 */
export function isFuturePriceList(priceList: PriceListOption, today: string): boolean {
  return priceList.startDate > today;
}

/**
 * The price list in effect today — the latest one starting on or before today, which is the one the
 * backend prices a stay booked right now from. Null when every price list is still to come, which is a
 * real state: the API stops an edit or a delete from leaving a lake with none started, but the *first*
 * price list of a lake can be created with a future start date.
 *
 * Relies on the newest-first order the API returns and {@link withPriceListInOrder} maintains, so the
 * first entry that has started is the latest one that has.
 */
export function activePriceListId(priceLists: readonly PriceListOption[]): number | null {
  const today = toDateOnly(new Date()) ?? '';
  return priceLists.find((priceList) => !isFuturePriceList(priceList, today))?.id ?? null;
}

/**
 * What a price-list dropdown starts on: the price list in effect today, not merely the newest one. Those
 * differ exactly when next season's increase has already been staged, and defaulting to the newest then
 * opens every grid on prices that are not yet charged — so an edit meant for today's prices silently
 * lands in the future list.
 *
 * Falls back to the price list taking effect soonest when none has started, since a dropdown with nothing
 * selected has no grid to show at all.
 */
export function defaultPriceListId(priceLists: readonly PriceListOption[]): number | null {
  return activePriceListId(priceLists) ?? priceLists.at(-1)?.id ?? null;
}

/**
 * Which price list a grid should be showing once its options have (re)loaded: the one the user was already
 * working in, or {@link defaultPriceListId} when there is none or it has since been deleted.
 *
 * A grid reloads its options after the page saves, and losing the user's place on each save — dropping them
 * back to today's price list while they were editing next season's — is what this exists to prevent.
 */
export function resolvePriceListId(priceLists: readonly PriceListOption[], current: number | null): number | null {
  if (current != null && priceLists.some((priceList) => priceList.id === current)) return current;
  return defaultPriceListId(priceLists);
}

/**
 * Highest price the API accepts, from the price DTOs' `[Range(0, 99999999.99)]`. Exceeding it is a hard
 * 400 whose body never reaches the user, so it is checked in the grid instead.
 */
export const MAX_PRICE = 99999999.99;

/**
 * Margin on a purchase/sale pair, as a percentage of the sale price. Null when it cannot be stated —
 * either price missing, or a sale of zero, which has no meaningful margin.
 *
 * Shared by every section that shows a purchase beside a sale — the season pricing grids and the
 * lake-level dog fee — so the number they display cannot drift apart.
 */
export function priceMargin(purchase: number | null, selling: number | null): number | null {
  if (purchase == null || selling == null || selling === 0) return null;
  return ((selling - purchase) / selling) * 100;
}
